// KidParentPanel — the PIN-gated settings sheet a parent uses while
// a kid is signed in on the device. Tapped via the gear icon in
// the top-right of KidModeShell; locked behind the same exit PIN.
//
// What's here in M1:
//   * Exit Kids Mode — sign out (formerly the door icon)
//   * Change / Set Exit PIN — re-uses set_kid_exit_pin RPC
//   * Remove Exit PIN — clear_kid_exit_pin RPC
//   * Pause / Resume Pet Hunger — set_pet_decay_paused RPC
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { petFallbackFor } from "./petSpriteMap";
import SendCards from "./SendCards";

export function KidParentPanel({ userId, pet, onClose, onExit }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [decayPaused, setDecayPaused] = useState(pet?.decay_paused === true);
  const [pinSheet, setPinSheet] = useState(false);
  const [hasPin, setHasPin] = useState(false);

  // The probe runs every mount so the row reflects current state
  // even after a parent set/cleared the PIN from outside Kids Mode.
  // Migration 0226 changed has_kid_exit_pin to require explicit
  // p_user_id (kid context, not auth.uid()).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      const { data } = await supabase.rpc("has_kid_exit_pin", { p_user_id: userId });
      if (!cancelled) setHasPin(data === true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Keep the toggle in sync if a card-earned trigger flips pause
  // state from the server side (won't happen today, but defensive).
  useEffect(() => {
    setDecayPaused(pet?.decay_paused === true);
  }, [pet?.decay_paused]);

  const exitKidsMode = async () => {
    setBusy(true);
    // Post-rework path: clear the parent's activeKidId state →
    // returns to the parent's home with their session intact. Falls
    // back to signOut() only when no onExit was provided (legacy
    // kid auth.users that hasn't been cleaned up yet).
    if (onExit) {
      onExit();
      onClose?.();
      setBusy(false);
      return;
    }
    await supabase.auth.signOut();
  };

  const togglePause = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const next = !decayPaused;
    const { error } = await supabase.rpc("set_pet_decay_paused", {
      p_kid_user_id: userId,
      p_paused: next,
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setDecayPaused(next);
  };

  const resetCraft = async () => {
    if (!userId) return;
    if (!confirm("Wipe THIS kid's craft progress?\n\n• Their inventory + XP + cards are cleared.\n• The shared recipe cache stays intact.\n• Pet identity is preserved.")) return;
    setBusy(true);
    setError(null);
    // NO p_wipe_global_discoveries flag — kid resets must never
    // touch the global emoji_combinations cache shared by all users.
    const { error } = await supabase.rpc("reset_kid_craft", {
      p_kid_user_id: userId,
    });
    setBusy(false);
    if (error) setError(error.message);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 240,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
        }}
      >
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            marginBottom: 4,
          }}
        >
          Grown-up settings
        </div>
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 12,
            color: COLOR.muted,
            marginBottom: 16,
          }}
        >
          Tap any item to change.
        </div>

        {/* Send Cards — pinned to the TOP of the panel because it's
            the most common reason a grown-up opens this sheet (giving
            a card to celebrate a meal / chore / day) and burying it
            below Pause Hunger + PetSwitcher made it un-findable. The
            sheet itself opens to a 2x2 card grid with +1/+3/+5 quick
            grants per card type (boost / spark / build / super).
            Each grant lands in the kid's reveal queue (migration
            0239) — pops a full-screen ✨ reveal on the kid's NEXT
            kid-mode open or live if already open. */}
        <SendCards userId={userId} />

        <PanelRow
          emoji={decayPaused ? "⏸️" : "🍽️"}
          title={decayPaused ? "Resume Pet Hunger" : "Pause Pet Hunger"}
          subtitle={decayPaused
            ? "Hunger isn't going down right now"
            : "Pet gets sleepy each day if not fed"}
          onClick={togglePause}
          busy={busy}
        />

        {/* Change-my-pet flow (migration 0222). Catalog-driven picker;
            calls set_kid_pet RPC. Resets card-state booleans on swap,
            preserves hunger + level (kid stats survive the change). */}
        <PetSwitcher userId={userId} pet={pet} />

        {/* Card-state toggles — direct flips for testing the
            alien-pet art. Stays for now; spending a real card via
            the kid tab also flips these. */}
        <CardStateToggles userId={userId} pet={pet} />

        <PanelRow
          emoji="🔒"
          title={hasPin ? "Change Exit PIN" : "Set Exit PIN"}
          subtitle={hasPin ? "Required to leave Kids Mode" : "Lock the exit door"}
          onClick={() => setPinSheet(true)}
        />

        {hasPin && (
          <PanelRow
            emoji="🗝️"
            title="Remove Exit PIN"
            subtitle="Exit will be one tap"
            danger
            onClick={async () => {
              setBusy(true);
              setError(null);
              const { error } = await supabase.rpc("clear_kid_exit_pin", { p_kid_user_id: userId });
              setBusy(false);
              if (error) setError(error.message);
              else setHasPin(false);
            }}
            busy={busy}
          />
        )}

        <PanelRow
          emoji="🔄"
          title="Reset Craft Progress"
          subtitle="Wipe Mix inventory + XP. Pet & cards stay."
          onClick={resetCraft}
          danger
          busy={busy}
        />

        <PanelRow
          emoji="🚪"
          title="Exit Kids Mode"
          subtitle="Sign out so another grown-up can sign in"
          onClick={exitKidsMode}
          danger
          busy={busy}
        />

        {error && (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 14,
            padding: 14,
            background: "transparent",
            color: COLOR.muted,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          DONE
        </button>
      </div>

      {pinSheet && (
        <SetExitPinInline
          userId={userId}
          hadPin={hasPin}
          onClose={() => setPinSheet(false)}
          onSaved={() => {
            setHasPin(true);
            setPinSheet(false);
          }}
        />
      )}
    </div>
  );
}

// PetSwitcher — catalog-driven pet picker for an existing kid. Tap a
// pet → calls set_kid_pet RPC → realtime UPDATE on kid_pet_state
// propagates to Pet.jsx, swapping the sprite without an app restart.
// Card-state booleans reset on swap (RPC-side); hunger + level
// preserved (kid stats survive the change).
function PetSwitcher({ userId, pet }) {
  const [open, setOpen] = useState(false);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadCatalog = async () => {
    if (pets.length > 0) return;
    setLoading(true);
    const { data, error: e } = await supabase
      .from("pet_catalog")
      .select("id, slug, name, image_url, species")
      .eq("rarity", "common")
      .order("sort_order", { ascending: true });
    setLoading(false);
    if (e) { console.warn("[PetSwitcher] catalog load:", e); return; }
    setPets(data || []);
  };

  const switchTo = async (petId) => {
    if (!userId || busy) return;
    setBusy(true);
    setError(null);
    const { error: e } = await supabase.rpc("set_kid_pet", {
      p_kid_user_id: userId,
      p_pet_id:      petId,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    setOpen(false);
  };

  const currentName = pet?.pet_name || pet?.pet_slug || "Pet";

  return (
    <>
      <PanelRow
        emoji="🪄"
        title="Change Pet"
        subtitle={`Currently: ${currentName}`}
        onClick={() => {
          setOpen(true);
          loadCatalog();
        }}
      />
      {open && (
        <div
          onClick={() => !busy && setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,18,14,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 260,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 480,
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#FAF6EE",
              borderRadius: RADIUS.sheet,
              padding: 20,
              boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
            }}
          >
            <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, marginBottom: 4 }}>
              Pick a new pet
            </div>
            <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted, marginBottom: 16 }}>
              The card progress resets. Hunger and level stay.
            </div>
            {loading && (
              <div style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted, padding: 8 }}>
                loading pets…
              </div>
            )}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))",
              gap: 10,
              marginBottom: 14,
            }}>
              {pets.map((p) => {
                const fb = petFallbackFor(p.slug);
                const active = pet?.kid_pet_id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => switchTo(p.id)}
                    disabled={busy || active}
                    aria-label={p.name || p.slug}
                    title={p.name || p.slug}
                    style={{
                      padding: 12,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      background: active ? COLOR.leafDeep : "#fff",
                      border: `2px solid ${active ? COLOR.leaf : COLOR.border}`,
                      borderRadius: 12,
                      cursor: busy ? "wait" : (active ? "default" : "pointer"),
                      opacity: busy ? 0.6 : 1,
                      touchAction: "manipulation",
                    }}
                  >
                    {p.image_url
                      ? <img src={p.image_url} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />
                      : <span style={{ fontSize: 36 }}>{fb.emoji}</span>}
                    <span style={{
                      fontFamily: FONT.mono,
                      fontSize: 10,
                      color: active ? "#fff" : COLOR.ink,
                      letterSpacing: "0.05em",
                    }}>
                      {(p.name || p.slug).toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>
            {error && (
              <div style={{
                marginBottom: 10,
                padding: "10px 12px",
                background: COLOR.roseDeep,
                border: `1px solid ${COLOR.roseDim}`,
                borderRadius: 8,
                color: COLOR.rose,
                fontFamily: FONT.forma,
                fontSize: 13,
              }}>
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={() => !busy && setOpen(false)}
              disabled={busy}
              style={{
                width: "100%",
                padding: 14,
                background: "transparent",
                color: COLOR.muted,
                border: `1px solid ${COLOR.border}`,
                borderRadius: 10,
                fontFamily: FONT.mono,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                cursor: busy ? "wait" : "pointer",
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function CardStateToggles({ userId, pet }) {
  const [busy, setBusy] = useState(false);
  const types = [
    { key: "boost", label: "Boost",  emoji: "⚡", on: pet?.has_boost === true },
    { key: "spark", label: "Spark",  emoji: "✨", on: pet?.has_spark === true },
    { key: "build", label: "Build",  emoji: "💪", on: pet?.has_build === true },
    { key: "super", label: "Super",  emoji: "🥦", on: pet?.has_super === true },
  ];
  const toggle = async (t) => {
    if (!userId || busy) return;
    setBusy(true);
    const { error } = await supabase.rpc("set_kid_pet_card_state", {
      p_kid_user_id: userId,
      p_card_type:   t.key,
      p_value:       !t.on,
    });
    setBusy(false);
    if (error) console.warn("[card-state] toggle:", error.message);
  };
  return (
    <div
      style={{
        marginBottom: 8,
        padding: 12,
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 12,
      }}
    >
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: "0.1em",
          fontWeight: 700,
          color: COLOR.sub,
          marginBottom: 8,
        }}
      >
        PET STATE (TESTING)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {types.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => toggle(t)}
            disabled={busy}
            style={{
              padding: "10px 4px",
              background: t.on ? COLOR.leafDeep : COLOR.deep,
              border: `2px solid ${t.on ? COLOR.leaf : COLOR.border}`,
              borderRadius: 10,
              cursor: busy ? "wait" : "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              touchAction: "manipulation",
            }}
          >
            <span style={{ fontSize: 22 }}>{t.emoji}</span>
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: t.on ? COLOR.leaf : COLOR.muted,
              }}
            >
              {t.label.toUpperCase()}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PanelRow({ emoji, title, subtitle, onClick, busy = false, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 14px",
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 12,
        marginBottom: 8,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.7 : 1,
        textAlign: "left",
        touchAction: "manipulation",
      }}
    >
      <span style={{ fontSize: 28, lineHeight: 1 }}>{emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 15,
            fontWeight: 600,
            color: danger ? COLOR.rose : COLOR.ink,
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 12,
            color: COLOR.muted,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </div>
      </div>
      <span style={{ color: COLOR.muted, fontSize: 18 }}>›</span>
    </button>
  );
}

// Same shape as AddKidSection.SetPinSheet but scoped to the
// currently-signed-in kid (no need to pass a target user_id —
// auth.uid() is enough because they're calling for themselves
// via set_kid_exit_pin's self-allowance branch).
function SetExitPinInline({ userId, hadPin, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!/^[0-9]{4,8}$/.test(pin)) { setError("PIN must be 4–8 digits"); return; }
    if (pin !== confirmPin) { setError("PINs don't match"); return; }
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc("set_kid_exit_pin", {
      p_kid_user_id: userId,
      p_pin: pin,
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    onSaved();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 260,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#FAF6EE",
          borderRadius: 20,
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.28)",
        }}
      >
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            marginBottom: 14,
          }}
        >
          {hadPin ? "Change Exit PIN" : "Set Exit PIN"}
        </div>

        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={8}
          value={pin}
          autoFocus
          placeholder="New PIN"
          onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
          style={pinInputStyle}
        />
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={8}
          value={confirmPin}
          placeholder="Confirm"
          onChange={(e) => setConfirmPin(e.target.value.replace(/[^0-9]/g, ""))}
          style={{ ...pinInputStyle, marginTop: 10 }}
        />

        {error && (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: 14,
              background: "transparent",
              color: COLOR.muted,
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || pin.length < 4 || confirmPin.length < 4}
            style={{
              flex: 2,
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: busy ? "wait" : "pointer",
              opacity: busy || pin.length < 4 || confirmPin.length < 4 ? 0.5 : 1,
            }}
          >
            {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

const pinInputStyle = {
  width: "100%",
  padding: "14px 16px",
  fontFamily: "inherit",
  fontSize: 22,
  letterSpacing: "0.35em",
  textAlign: "center",
  background: "#fff",
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  color: COLOR.ink,
  boxSizing: "border-box",
};

export default KidParentPanel;
